// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback, useMemo, useState, type ReactNode } from 'react';

import type { EvolveApiBaseRoute, EvolveApiBody, AdditionalRequestConfig } from '@evolvemep/api-ts';
import type { IRowNode, GridApi, ICellRendererParams } from 'ag-grid-enterprise';

import { type AgGridContainerProps, AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { isNil } from 'helpers/isNotNil';
import { strongKeys } from 'helpers/strongEntries';
import { useGeneralContext } from 'helpers/useGeneralContext';

export type AgGridEditableProps<
  TData,
  PatchRoute extends EvolveApiBaseRoute<'PATCH'>,
  PatchBody extends EvolveApiBody<'PATCH', PatchRoute>,
> = {
  /**
   * The `GridApi` of the parent AG Grid
   *
   * Note: This field is actually unused,
   * but helps validate that the Grid data type `TData` matches that of the Editable config `TData`
   * */
  gridApi: GridApi<TData> | null;
  /**
   * Used to convert the row data into the request body of the patch call
   */
  requestConverter: (row: TData, updatedData: Partial<TData>) => PatchBody;
  /**
   * Used to create the required request config for the Patch call using the given `row`
   */
  configMapper: (row: TData) => AdditionalRequestConfig<'PATCH', PatchRoute>;
  /**
   * The patch call used to actually update the row data
   */
  patchCall: (body: PatchBody, config: AdditionalRequestConfig<'PATCH', PatchRoute>) => Promise<TData | null>;
  /**
   * Whether or not to render the children inside of an `AgGridContainer`
   *
   * @default true
   */
  withContainer?: boolean;
  /**
   * The `colId` of any column sorted by default by the backend
   * (without needing to be selected in the grid)
   */
  defaultSortedColId?: string;
} & (
  | ({
      withContainer?: true;
    } & AgGridContainerProps)
  | {
      withContainer: false;
      children: ReactNode;
    }
);

type AgGridEditableContextType<TData> = {
  /**
   * Used to update a given row with new partial data.
   *
   * This will call `patchCall` with the new data
   * and update the row data in the parent AG Grid.
   *
   * @returns The updated row data
   */
  updateRow: (
    /** The updated data on the row */
    updateData: Partial<TData>,
    /** The row's renderer params */
    rendererParams: ICellRendererParams<TData>,
  ) => Promise<TData | null>;
  /** The `id` of the `RowNode` currently being edited (if any) */
  rowMutationRunning: IRowNode<TData>['id'];
};

const AgGridEditableContext = React.createContext<
  // oxlint-disable-next-line typescript/no-explicit-any
  AgGridEditableContextType<any> | undefined
>(undefined);

/**
 * Used to wrap an AG Grid in an editable context.
 * This allows you to use `editableColDef` to define editable columns.
 */
export const AgGridEditable = <
  TData,
  PatchRoute extends EvolveApiBaseRoute<'PATCH'>,
  PatchBody extends EvolveApiBody<'PATCH', PatchRoute>,
>({
  patchCall,
  requestConverter,
  configMapper,
  gridApi,
  defaultSortedColId,
  ...props
}: AgGridEditableProps<TData, PatchRoute, PatchBody>) => {
  const [rowMutationRunning, setRowMutationRunning] = useState<IRowNode<TData>['id']>(undefined);
  const updateRow = useCallback<AgGridEditableContextType<TData>['updateRow']>(
    async (updatedData, { node, api, column }) => {
      if (isNil(node.data)) throw new Error('Node data is nil');
      const body = requestConverter(node.data, updatedData);
      setRowMutationRunning(node.id);
      return patchCall(body, configMapper(node.data))
        .then((res) => {
          const sortedAndFilteredColumns = [
            ...(defaultSortedColId ? [defaultSortedColId] : []),
            ...strongKeys(gridApi?.getFilterModel() ?? {}),
            ...(gridApi
              ?.getColumnState()
              .filter((c) => !!c.sort)
              .map((c) => c.colId) ?? []),
          ];
          if (res) node.updateData(res);

          const colId = column?.getColId();
          // If we modified the data in a sorted/filtered column,
          // we need to refresh the entire grid to reflect the pagination changes.
          if (colId && sortedAndFilteredColumns.includes(colId)) {
            refreshGrid(api);
          }
          return res;
        })
        .finally(() => setRowMutationRunning(undefined));
    },
    [requestConverter, patchCall, configMapper, gridApi, defaultSortedColId],
  );

  const value = useMemo<AgGridEditableContextType<TData>>(
    () => ({
      updateRow,
      rowMutationRunning,
    }),
    [updateRow, rowMutationRunning],
  );

  return (
    <AgGridEditableContext value={value}>
      {/* Check `false` because undefined should be treated as `true` */}
      {props.withContainer === false ? props.children : <AgGridContainer {...props} />}
    </AgGridEditableContext>
  );
};

/** Do not call this directly. Use `editableColDef` instead. */
export const useAgGridEditable = <T,>() =>
  useGeneralContext(AgGridEditableContext, 'AgGridEditable') as AgGridEditableContextType<T>;
